import React from 'react';
import { SimpleSet, has, toggle } from 'helpers/setOperations';
import { MakeOptional } from '@sift/skipper/helpers/typings';

// Components
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Button, { ButtonProps } from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Divider from '@material-ui/core/Divider';

// Styles
import classNames from 'classnames';
import withStyles from '@material-ui/core/styles/withStyles';
import { defineStyles, Classes } from '@sift/skipper/styles/defineStyles';

const styles = defineStyles(theme => {
  return {
    root: {
      position: 'relative',
      display: 'inline',
      zIndex: theme.zIndex.surface,
    },
    attributeSelector: {
      position: 'absolute',
      top: `calc(100% + ${theme.spacing(1)}px)`,
      right: 0,
      display: 'flex',
      flexDirection: 'column',
      width: 363,
      maxHeight: 478,
      paddingTop: theme.spacing(1),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      flex: '0 0 auto',
      alignItems: 'center',
      padding: theme.spacing(0, 2),
    },
    title: {
      fontSize: 16,
      fontWeight: 500,
      margin: theme.spacing(1, 0),
    },
    search: {
      flex: '0 0 auto',
      padding: theme.spacing(0, 2),
    },
    attributes: {
      flex: '1 1 auto',
      overflow: 'auto',
    },
    attributeRow: {
      display: 'flex',
      padding: theme.spacing(0, 1),
    },
    actions: {
      flex: '0 0 auto',
      display: 'flex',
      padding: theme.spacing(1),
    },
    clearAll: {
      marginRight: theme.spacing(1),
    },
    cancel: {
      marginLeft: 'auto',
      marginRight: theme.spacing(1),
    },
    save: {},
    afterListView: {},
    attribute: {},
  };
});

export interface AttributeSelectorBaseProps<T> {
  className?: string;
  classes: Classes<typeof styles>;
  searchPlaceholder?: string;
  open: boolean;
  label: string;
  title: string;
  selectedAttributes: SimpleSet;
  attributes: T[];
  saveButtonLabel?: string;
  /**
     * renders the header of the component. This function takes in the props
     * being passed into it
     * the default implementation of this is:
     *
     * ```jsx
     thisProps => {
        const { classes, title } = thisProps;
        return (
          <div className={classes.header}>
            <div className={classes.title}>{title}</div>
          </div>
        );
      }
    ```
     */
  renderHeader: (props: AttributeSelectorBaseProps<T>) => React.ReactNode;
  /**
   * use this prop to render the attribute row. the default implementation
   * of this is `attribute => attribute.toString()`;
   *
   * ```jsx
   * <AttributeSelector
   *    renderAttribute={field => field.displayName}
   *  />
   * ```
   */
  renderAttribute: (t: T) => React.ReactNode;
  /**
   * use this to render a list wrapper. the normal list element as well
   * as `this.props` is passed into this wrapper
   *
   * (defaultElement, attributeSelectorBaseProps) => React.Element
   */
  listWrapper: (
    defaultElement: React.ReactNode,
    props: AttributeSelectorBaseProps<T>,
  ) => React.ReactNode;
  afterListView?: React.ReactNode;
  /**
   * use this props to pick the key of an item in the attribute array. e.g.
   * if you had an array of fields, you'd pass in something like:
   *
   * ```jsx
   * <AttributeSelector
   *   getAttributeKey={field => field.key}
   * />
   * ```
   */
  getAttributeKey: (t: T) => string;
  /**
   * callback that is invoked when the "Save" button is clicked. this
   * function is called with no arguments.
   */
  onSave: () => void;
  /**
   * callback invoked when the label is clicked to open the selector
   */
  onOpen: () => void;
  /**
   * callback that is invoked when the "Close" button is clicked, the user
   * clicks away, or the save button is pressed
   */
  onClose: () => void;
  /**
   * callback that's invoked when the "Clear all" button is clicked
   */
  onClearAll: () => void;
  /**
   * fires when the user types in the search box. takes in a string of
   * the query
   */
  onSearch: (query: string) => void;
  /**
   * fires whenever an attribute selection changes. this fires with the
   * selected attributes set (implemented with an object).
   *
   * note: since this component is controlled, you must pass the newly
   * selected attributes back into this component or no update will be seen,
   */
  onChangeSelectedAttributes: (set: SimpleSet) => void;
  LabelButtonProps?: ButtonProps;
}

/**
 * a completely controlled attribute selector component. don't use this
 * component by itself, instead use the `Local` or `Server` implementations
 */
class AttributeSelectorBase<T = any> extends React.PureComponent<AttributeSelectorBaseProps<T>> {
  static defaultProps = {
    renderAttribute: (attribute: any) => attribute.toString(),
    renderHeader: (thisProps: AttributeSelectorBaseProps<any>) => {
      const { classes, title } = thisProps;
      return (
        <div className={classes.header}>
          <div className={classes.title}>{title}</div>
        </div>
      );
    },
    listWrapper: (listElm: any) => listElm,
  };
  renderAriaLabel = (attribute: any) => attribute.displayName;
  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const value = e.currentTarget.value || '';
    this.props.onSearch(value);
  };

  handleToggle(key: string) {
    const { onChangeSelectedAttributes, selectedAttributes } = this.props;
    onChangeSelectedAttributes(toggle(selectedAttributes, key));
  }

  handleSave = () => {
    this.props.onClose();
    this.props.onSave();
  };

  toggleOpen = () => {
    const { open, onClose, onOpen } = this.props;
    if (open) {
      onClose();
    } else {
      onOpen();
    }
  };

  render() {
    const {
      attributes,
      className,
      classes,
      label,
      searchPlaceholder,
      LabelButtonProps,
      getAttributeKey,
      open,
      selectedAttributes,
      onClearAll,
      onClose,
      onOpen,
      afterListView,
      renderHeader,
      listWrapper,
      renderAttribute,
      saveButtonLabel,
    } = this.props;

    return (
      <div className={classNames(classes.root, className)}>
        <Button onClick={this.toggleOpen} {...LabelButtonProps}>
          {label}
        </Button>
        <Paper
          elevation={2}
          className={classes.attributeSelector}
          style={{ display: open ? undefined : 'none' }}
        >
          {renderHeader(this.props)}

          {searchPlaceholder && (
            <TextField
              className={classes.search}
              id="search"
              type="search"
              placeholder={searchPlaceholder}
              onChange={this.handleChange}
            />
          )}

          <Divider />

          {listWrapper(
            <List className={classes.attributes}>
              {attributes
                .map(attribute => ({
                  attribute,
                  key: getAttributeKey(attribute),
                }))
                .map(({ attribute, key }) => (
                  <ListItem
                    button
                    key={key}
                    className={classes.attributeRow}
                    onClick={() => this.handleToggle(key)}
                  >
                    <Checkbox
                      checked={has(selectedAttributes, key)}
                      inputProps={{ 'aria-label': `${this.renderAriaLabel(attribute)}-checkbox` }}
                    />
                    <div className={classes.attribute}>{renderAttribute(attribute)}</div>
                  </ListItem>
                ))}
              {afterListView}
            </List>,
            this.props,
          )}

          <Divider />

          <div className={classes.actions}>
            <Button className={classes.clearAll} onClick={onClearAll}>
              Clear All
            </Button>
            <Button className={classes.cancel} onClick={onClose}>
              Cancel
            </Button>
            <Button
              className={classes.save}
              onClick={this.handleSave}
              color="secondary"
              variant="contained"
            >
              {saveButtonLabel || 'Save'}
            </Button>
          </div>
        </Paper>
      </div>
    );
  }
}

export default withStyles(styles)(AttributeSelectorBase) as <T extends any>(
  props: MakeOptional<
    AttributeSelectorBaseProps<T>,
    'classes' | 'renderHeader' | 'renderAttribute' | 'listWrapper'
  >,
) => any;
